<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { ZodType } from "zod";
import { UiButton, UiForm, UiFormField, UiInfoBox, UiInputField, UiText, UiUnitNumberField } from "@shopexperts/shared";
import type { UiUnitNumberFieldUnit } from "@shopexperts/shared";
import {
  billingOptionFormSchema,
  formatBillingInterval,
  formatRecurrence,
  type BillingIntervalUnit,
  type BillingOptionFormValues,
} from "@/app/composables/expert-billing/billingOptionForm";
import { dollarsToCents, formatPrice } from "@/app/composables/expert-plans/expertPlanForm";

const props = defineProps<{
  planName: string;
  initialValues: BillingOptionFormValues;
  isEditing?: boolean;
  isSubmitting?: boolean;
}>();

const emit = defineEmits<{
  (e: "submit", value: BillingOptionFormValues): void;
  (e: "cancel"): void;
}>();

// Numeric fields hold strings while editing (the inputs emit strings); the
// zod schema coerces on submit, so the emitted values are proper numbers.
interface BillingOptionFormModel {
  label: string;
  interval_count: string;
  interval_unit: BillingIntervalUnit;
  amount_dollars: string;
}

// UiForm seeds vee-validate from initialValues (not from the bound model)
// and writes those values back into the model on mount, so the same object
// must be passed as both.
const initialFormValues: BillingOptionFormModel = {
  label: props.initialValues.label,
  interval_count: String(props.initialValues.interval_count),
  interval_unit: props.initialValues.interval_unit,
  amount_dollars: props.initialValues.amount_dollars > 0 ? String(props.initialValues.amount_dollars) : "",
};

const formModel = ref<BillingOptionFormModel>({ ...initialFormValues });
const isDirty = ref(false);

// The form edits strings; the schema's coerced OUTPUT is BillingOptionFormValues.
// UiForm's generic can't express input≠output, so it is keyed to the editing
// model and the submit payload is cast back to the parsed shape.
const formSchema = billingOptionFormSchema as unknown as ZodType<BillingOptionFormModel>;

const intervalUnits: readonly UiUnitNumberFieldUnit[] = [
  { label: "Weeks", value: "week" },
  { label: "Months", value: "month" },
  { label: "Years", value: "year" },
];

// The unit dropdown lives inside the interval field; route it through the
// form model so vee-validate stays the single source of truth.
const unitProxy = computed<string | undefined>({
  get: () => formModel.value?.interval_unit,
  set: (unit) => {
    if (!formModel.value || (unit !== "week" && unit !== "month" && unit !== "year")) return;
    formModel.value = { ...formModel.value, interval_unit: unit };
  },
});

function suggestionFor(unit: unknown, count: unknown): string | null {
  if (unit !== "week" && unit !== "month" && unit !== "year") return null;
  const parsed = Number(count);
  if (!Number.isFinite(parsed) || parsed < 1) return null;
  return formatRecurrence({ interval_unit: unit, interval_count: parsed });
}

// Keep the label suggestion in step with the recurrence while creating —
// stop the moment the admin types their own label.
watch(
  () => [formModel.value?.interval_unit, formModel.value?.interval_count] as const,
  (next, prev) => {
    if (props.isEditing || !formModel.value) return;
    const suggested = suggestionFor(next[0], next[1]);
    if (!suggested) return;
    const current = (formModel.value.label ?? "").trim();
    const previousSuggestion = prev ? suggestionFor(prev[0], prev[1]) : null;
    if ((current === "" || current === previousSuggestion) && suggested !== current) {
      formModel.value = { ...formModel.value, label: suggested };
    }
  },
);

const previewName = computed(() => {
  const label = (formModel.value?.label ?? "").trim();
  return label ? `${props.planName} - ${label}` : null;
});

const previewOffer = computed(() => {
  const count = Number(formModel.value?.interval_count);
  const amount = Number(formModel.value?.amount_dollars);
  if (!Number.isFinite(count) || count < 1) return null;
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const interval = formatBillingInterval({
    interval_unit: formModel.value.interval_unit,
    interval_count: count,
  });
  return `${formatPrice(dollarsToCents(amount), "USD")} billed ${interval}`;
});

function onSubmit(values: BillingOptionFormModel) {
  emit("submit", values as unknown as BillingOptionFormValues);
}
</script>

<template>
  <UiForm
    v-model="formModel"
    v-model:dirty="isDirty"
    :schema="formSchema"
    :initial-values="initialFormValues"
    scroll-to-first-error
    @submit="onSubmit"
  >
    <div class="flex flex-col gap-5 max-w-xl">
      <UiFormField
        name="label"
        label="Label"
        hint="Names the option for experts and the Stripe product."
      >
        <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error, disabled }">
          <UiInputField
            :id="id"
            name="label"
            placeholder="e.g., Quarterly"
            :model-value="(modelValue as string)"
            :invalid="invalid"
            :error="error"
            :disabled="disabled"
            @update:model-value="onUpdateModelValue"
            @validate="onValidate"
          />
        </template>
      </UiFormField>

      <div class="flex flex-col gap-5">
        <UiFormField
          name="interval_count"
          label="Billed every"
          hint="Every 1 month is monthly; every 3 months is quarterly."
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiUnitNumberField
              :id="id"
              name="interval_count"
              :units="intervalUnits"
              :min="1"
              :max="65535"
              :step="1"
              :model-value="String(modelValue ?? '')"
              v-model:unit="unitProxy"
              :invalid="invalid"
              :error="error"
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
            />
          </template>
        </UiFormField>

        <UiFormField name="amount_dollars" label="Amount per period (USD)">
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error, disabled }">
            <UiInputField
              :id="id"
              name="amount_dollars"
              type="number"
              placeholder="0"
              :min="0"
              :step="1"
              :model-value="String(modelValue ?? '')"
              :invalid="invalid"
              :error="error"
              :disabled="disabled"
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
            />
          </template>
        </UiFormField>
      </div>

      <!-- Live preview: exactly what the option becomes when saved -->
      <UiInfoBox variant="info" title="Expert-facing offer">
        <div class="flex flex-col gap-0.5">
          <UiText v-if="previewName" as="p" variant="p-small" class="font-semibold text-se-dark">
            {{ previewName }}
          </UiText>
          <UiText as="p" variant="caption-normal" class="text-se-gray-700">
            {{ previewOffer ?? "Fill in the amount and billing period to complete the offer." }}
          </UiText>
        </div>
      </UiInfoBox>

      <UiText
        v-if="props.isEditing"
        as="p"
        variant="caption-normal"
        class="text-se-gray-600"
        text="If this option is sellable, saving synchronizes the change to Stripe automatically. Existing subscriptions keep their historical price."
      />

      <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-1">
        <UiButton variant="outline" label="Cancel" :disabled="props.isSubmitting" @click="emit('cancel')" />
        <UiButton
          type="submit"
          variant="primary"
          :label="props.isSubmitting ? 'Saving...' : props.isEditing ? 'Save option' : 'Add option'"
          :disabled="props.isSubmitting || !isDirty"
        />
      </div>
    </div>
  </UiForm>
</template>
